"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PortHolder = void 0;
exports.isPortHolder = isPortHolder;
/**
 * SOLID Review
 * S — Strong. One concern: mark an object as a bag of ports, and answer whether an unknown value
 *     is one. It holds no ports itself and knows nothing about what a Port is.
 * O — Strong. Nothing here varies. A component groups its ports by extending this; describePorts
 *     needs no change per holder.
 * L — Strong. Every subclass is substitutable for the base by construction — the base declares one
 *     symbol-keyed brand and no behaviour, so there is nothing for a subclass to weaken.
 * I — Strong. One class and one predicate.
 * D — Strong. Depends on nothing, not even Port. That is deliberate: making the brand independent
 *     of the port types keeps this file out of the module-identity problem described below.
 */
/**
 * A grouped bag of ports, walked by `describePorts` as if its fields were the component's own.
 *
 * ⚠ WHY THIS EXISTS: A NON-RECURSIVE WALK COST SIX COMPONENTS A HAND-WRITTEN METHOD.
 * `describePorts` did ONE `Object.entries()` pass filtered by `instanceof Port`. A component that
 * grouped its ports into holder objects was therefore invisible to it — the walk returned `[]`,
 * silently, so the component would vanish from every manifest and every generated diagram with
 * nothing throwing. Six components paid for that with a hand-written `describeOwnPorts()` that no
 * interface required, twenty call sites that had to remember to use it, three tests written purely
 * to pin the empty result, and a ⚠ warning repeated in seven file headers.
 *
 * ⚠ AND WHY IT IS A MARKER RATHER THAN "RECURSE INTO ANY OBJECT FIELD", which is what the review
 * that queued this work first proposed. Measured on the backend, blind recursion is WRONG:
 * `ComponentApplication` holds `private domain!: ComponentDomain`, and `ComponentDomain` declares
 * `DomainIdGenerator` and `DomainSurface`. One level of untyped recursion would have attributed
 * both of the domain's ports to the application component, in the committed manifest and in the
 * diagram — trading a silent empty for a silent wrong, which is worse. `ComponentDomain` also
 * holds five domain-service objects that have nothing to do with ports.
 *
 * A structural test ("an object all of whose fields are Ports") was rejected for the same reason
 * the original design failed: adding one non-port field to a holder would silently un-hold it. The
 * brand is explicit, so the failure mode is a compile error rather than a shorter manifest.
 */
/**
 * ⚠ `Symbol.for`, NOT `Symbol()`, AND NOT `instanceof`. The two required-port variants have their
 * own package subpaths, so under Vite's dependency pre-bundling the kernel can be instantiated
 * TWICE and a value built against one instance is not `instanceof` the class the other imported —
 * this is the failure that forced capability detection on the port guards in describe.ts (§28.42).
 * `Symbol.for` reads from the cross-realm global registry, so the brand survives it; a plain
 * `Symbol()` would be a second private symbol per module instance and reintroduce the bug.
 */
const PORT_HOLDER = Symbol.for('@ml3d/kernel.PortHolder');
class PortHolder {
    /**
     * SYMBOL-KEYED SO `Object.entries` NEVER SEES IT. The walk in describe.ts enumerates string keys;
     * a string-keyed brand would appear as a field on every holder and would have to be filtered out
     * by name — one more thing to remember, in the file whose whole problem was a thing to remember.
     */
    [PORT_HOLDER] = true;
}
exports.PortHolder = PortHolder;
/** Whether a value is a bag of ports that `describePorts` should walk into. */
function isPortHolder(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value[PORT_HOLDER] === true);
}
//# sourceMappingURL=PortHolder.js.map